// src/lib/leads/leadEventSchema.ts

export const LEAD_EVENT_TYPES = [
  'view',
  'contact_click',
  'interest_click',
  'lead_captured',
  'booking_request',
  'payment_started',
  'payment_completed',
] as const;

export type LeadEventType = (typeof LEAD_EVENT_TYPES)[number];

// Reserve product keys ahead of launches so telemetry remains stable from day one.
export const FUTURE_LEAD_PRODUCTS = [
  'product-01',
  'product-02',
  'product-03',
  'product-04',
  'product-05',
  'product-06',
  'product-07',
  'product-08',
  'product-09',
  'product-10',
  'product-11',
  'product-12',
  'product-13',
  'product-14',
] as const;

export const LEAD_PRODUCTS = [
  'stay',
  'restaurant',
  'fixit',
  'professional-services',
  'service',
  ...FUTURE_LEAD_PRODUCTS,
  'other',
] as const;

export type LeadProduct = (typeof LEAD_PRODUCTS)[number];

export const isLeadProduct = (value: string): value is LeadProduct =>
  (LEAD_PRODUCTS as readonly string[]).includes(value);

export const LEAD_DASHBOARD_GROUPS: Record<LeadProduct, 'stay' | 'restaurant' | 'fixit' | 'service' | 'other'> = {
  stay: 'stay',
  restaurant: 'restaurant',
  fixit: 'fixit',
  'professional-services': 'service',
  service: 'service',
  'product-01': 'other',
  'product-02': 'other',
  'product-03': 'other',
  'product-04': 'other',
  'product-05': 'other',
  'product-06': 'other',
  'product-07': 'other',
  'product-08': 'other',
  'product-09': 'other',
  'product-10': 'other',
  'product-11': 'other',
  'product-12': 'other',
  'product-13': 'other',
  'product-14': 'other',
  other: 'other',
};

export interface LeadEventPayload {
  profileId: string;
  product: LeadProduct;
  eventType: LeadEventType;
  source?: string | null;
  viewerId?: string | null;
  viewerWhatsapp?: string | null;
  viewerEmail?: string | null;
  viewerSessionId?: string | null;
  viewerDeviceId?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface LeadFunnelWindowStats {
  totalViews: number;
  /** Distinct `interest_click` events (e.g. menu/map/availability opens). */
  interestClicks: number;
  /** Distinct `contact_click` events (e.g. WhatsApp / order / call intents). */
  contactClicks: number;
  /** Distinct `booking_request` events. */
  bookingRequests: number;
  /** Interest + contact + booking. Kept for back-compat with older consumers. */
  totalInterestClicks: number;
  uniqueInterestedVisitors: number;
  leadsCaptured: number;
}

export interface LeadFunnelStats {
  current: LeadFunnelWindowStats;
  previous: LeadFunnelWindowStats;
}

export const resolveLeadProductFromProfileType = (profileType: unknown): LeadProduct => {
  const value = String(profileType || '').trim().toLowerCase();
  if (!value) return 'other';
  if (value.includes('stay')) return 'stay';
  if (value.includes('restaurant')) return 'restaurant';
  if (value.includes('fix-it') || value.includes('fixit')) return 'fixit';
  if (value.includes('professional services') || value.includes('professional-services')) return 'professional-services';
  if (value.includes('service')) return 'service';
  return 'other';
};
